export interface NeucleosWorldSummary {
  readonly worldKey: string;
  readonly name?: string;
}

/**
 * The `?world=` value that names the explicit Local (ungoverned) door.
 * Operating mode is a choice separate from sign-in: a signed-in member may
 * run the traditional harness without entering any World. World keys are
 * `world-…` shaped, so this sentinel cannot collide with a membership.
 */
export const LOCAL_WORLD_DOOR = "local";

/**
 * The `?world=` value that names the ALL stance: the whole entitlement,
 * before any World filter is applied. A World is a filter over the one
 * entitled dataset; ALL is that dataset unfiltered. World keys are
 * `world-…` shaped, so this sentinel cannot collide with a membership.
 */
export const ALL_WORLDS_DOOR = "all";

export type NeucleosWorldSelection =
  | { status: "selected"; worldKey: string }
  | { status: "all"; worldKey: null }
  | { status: "local"; worldKey: null }
  | { status: "unavailable"; worldKey: null; message: string };

export function isLocalWorldSelection(selection: NeucleosWorldSelection | null): boolean {
  return selection?.status === "local";
}

export function worldDisplayName(
  worlds: ReadonlyArray<NeucleosWorldSummary>,
  worldKey: string | null,
): string {
  if (!worldKey) return "this World";
  const match = worlds.find((world) => world.worldKey === worldKey);
  return match?.name?.trim() || worldKey;
}

// A World switch is a root navigation, not a session move. Carry only the
// destination World. Cleo-code's pathname is the thread — that is World-owned
// address, so it does not ride the switch the way the cockpit's `/` does.
// The destination restores its own room after the load.
export function worldSelectionHref(destinationPath: string, worldKey: string): string {
  const path = destinationPath.trim().split("?")[0] || "/";
  const room = path.startsWith("/") ? path : `/${path}`;
  const next = new URLSearchParams({ world: worldKey });
  return `${room}?${next.toString()}`;
}

export function resolveWorldSelection(
  worlds: ReadonlyArray<NeucleosWorldSummary>,
  requestedWorldKey: string,
  rememberedDoor = "",
  /**
   * RM-CAN-14: the stance the Desk last stood in, kept apart from World
   * memory. A relaunch loads a bare URL; when the person last CHOSE Local,
   * the bare URL answers Local again, so a restart returns the same stance.
   * Only the explicit Local choice is remembered — never signed-out or
   * zero-World Local, which are not choices — and ALL is not.
   */
  rememberedStance = "",
  /**
   * The name of the requested World when the link carries one (Open in Cleo on
   * the purchase thank-you page). A World the Desk holds no grant for yet is
   * then "sign in again to open it", not a membership refusal.
   */
  requestedWorldName = "",
): NeucleosWorldSelection {
  const requested =
    requestedWorldKey.trim() || (rememberedStance === LOCAL_WORLD_DOOR ? LOCAL_WORLD_DOOR : "");
  if (requested === LOCAL_WORLD_DOOR) return { status: "local", worldKey: null };
  if (requested === ALL_WORLDS_DOOR) return { status: "all", worldKey: null };
  if (requested) {
    const match = worlds.find((world) => world.worldKey === requested);
    return match
      ? { status: "selected", worldKey: match.worldKey }
      : {
          status: "unavailable",
          worldKey: null,
          message: requestedWorldName.trim()
            ? `Sign in again to open ${requestedWorldName.trim()}.`
            : `Requested World '${requested}' is not an active membership.`,
        };
  }
  // No request: never ask. Selection means only which tab is active
  // (principal correction, 2026-08-25) and every entitled World loads its
  // room the same way, so the remembered last door answers, else the first
  // entitled World. A stale memory is ignored, never surfaced as a question.
  // Local sits outside World memory (F03/W42): only membership keys restore.
  // A legacy stored Local sentinel therefore reads as stale and falls through.
  const remembered = rememberedDoor.trim();
  const rememberedMatch = remembered
    ? worlds.find((world) => world.worldKey === remembered)
    : undefined;
  if (rememberedMatch) return { status: "selected", worldKey: rememberedMatch.worldKey };
  if (worlds.length > 0) return { status: "selected", worldKey: worlds[0]!.worldKey };
  return {
    status: "unavailable",
    worldKey: null,
    message: "The authenticated Actor has no active World memberships.",
  };
}

/**
 * The Local operating mode is active when it is explicitly selected, when no
 * one is signed in, or when the signed-in Actor reaches zero Worlds (W06
 * corrected: zero Worlds yields the standard Local app — never an unscoped
 * World and never a blank product). Governed scope exists only where a
 * membership actually stands behind the surface. This is the ONE mode
 * derivation; components must not re-derive it from nullness (F04).
 */
export function localModeActive(state: {
  readonly signedIn: boolean;
  readonly worldCount: number;
  readonly selection: NeucleosWorldSelection | null;
}): boolean {
  if (state.selection?.status === "local") return true;
  if (!state.signedIn) return true;
  return state.worldCount === 0;
}

/**
 * The ALL stance is active when explicitly selected. Like `localModeActive`
 * this is the ONE derivation (F04); components must not re-read the status
 * string themselves.
 */
export function allModeActive(selection: NeucleosWorldSelection | null): boolean {
  return selection?.status === "all";
}

export interface WorldThreadScope {
  readonly activeWorldKey: string | null;
  /** The explicit Local mode: only ungoverned (World-less) work belongs. */
  readonly local?: boolean;
  /** The ALL stance: every thread, before any World filter is applied. */
  readonly all?: boolean;
}

/**
 * Exact World identity at every membership count (O26/W44). Tenant isolation
 * never varies with how many Worlds the Actor reaches: a sole membership
 * admits no unbound thread and no foreign thread, and Local admits exactly
 * the World-less threads. The former worldCount<=1 bypass is deleted (F05).
 */
export function threadBelongsToWorld(
  threadWorldKey: string | null | undefined,
  input: WorldThreadScope,
): boolean {
  // ALL is all: the entitlement before any filter admits every thread.
  if (input.all === true) return true;
  if (input.local === true) return threadWorldKey == null;
  if (!input.activeWorldKey) return false;
  if (!threadWorldKey) return false;
  return threadWorldKey === input.activeWorldKey;
}
